<script setup lang="ts">
import NProgress from 'nprogress'

import { EmployerInviteType } from '@swipe4work/models-orval'
import { FirebaseAuthService } from '@/services/auth/firebase-auth-service'
import { AuthProvider } from '@/services/auth/auth-service'
import { AuthState } from '@/stores/auth-store'
import { captureException } from '@sentry/vue'
import RegisterContent from './components/RegisterContent.vue'

definePage({
  path: 'register',
  name: '/onboarding/register',
  meta: {
    layout: 'false',
    allowAnonymous: true,
  },
})

const authStore = useAuthStore()

const onboardingStore = useOnboardingStore()
const { invite, inviteId, inviteType } = storeToRefs(onboardingStore)

const { isFirebaseAuth } = storeToRefs(authStore)

const router = useRouter()

onMounted(() => {
  if (isFirebaseAuth.value) {
    router.replace({
      name: '/onboarding/company',
    })
  }
})

const isSubmitting = ref(false)

async function register(entity: {
  employerFirstName: string,
  employerLastName: string,
  password: string 
}) {
  NProgress.start()
  isSubmitting.value = true
  const token = await onboardingStore.createEmployer(
    {
      firstName: entity.employerFirstName,
      lastName: entity.employerLastName,
      password: entity.password,
      inviteId: inviteId.value,
    },
  )
  if (token) {
    try {
      const firebaseAuthService = await authStore.getAuthService<FirebaseAuthService>(AuthProvider.Firebase, FirebaseAuthService, authStore.firebaseAuth)

      await firebaseAuthService.loginWithToken(token)
    } catch (error) {
      captureException(error)
      authStore.setAuthState(AuthState.AuthenticationError)
    }
    await authStore.fetchEmployerAccess()
    if (invite.value?.type === EmployerInviteType.Teammate) {
      await onboardingStore.finishOnboarding()
    } else {
      onboardingStore.goToPage(1)
    }
  }
  isSubmitting.value = false
  NProgress.done()
}
</script>

<template>
  <RegisterContent
    :invite-type="inviteType"
    :invited-by="invite?.invitedBy"
    :invited-by-company="invite?.invitedByCompany"
    :email="invite?.email"
    :first-name="invite?.firstName"
    :last-name="invite?.lastName"
    :is-submitting="isSubmitting"
    @register="register"
  />
</template>
